import { jsx } from 'react/jsx-runtime';
import { createContext, useState, useEffect } from 'react';
import { LOCAL_STORAGE_KEY, AM_STORAGE_KEY } from '../helpers/constants.js';
import { loginRedirect, acquireToken, getFeatures, getUserDomains, getCurrentUser, getUserAvatarName, isAMUser, saveUserDomain } from '../helpers/utils.js';
import { Loading } from '@geit/ui-components';
import { formatFullName } from '../helpers/format.js';
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, {
  enumerable: true,
  configurable: true,
  writable: true,
  value
}) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {})) if (__hasOwnProp.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols) for (var prop of __getOwnPropSymbols(b)) {
    if (__propIsEnum.call(b, prop)) __defNormalProp(a, prop, b[prop]);
  }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const emptyValue = {
  config: {
    app: "empower",
    isExternal: false,
    requireApigeeToken: false
  },
  token: {},
  user: {},
  modules: [],
  setToken: () => {},
  cachedPermissions: {},
  setCachedPermissions: () => {},
  updateUser: () => {},
  refreshUser: () => Promise.resolve({}),
  refreshPage: () => {},
  refreshKey: 0
};
const AuthenticationContext = createContext(emptyValue);
AuthenticationContext.displayName = "AuthenticationContext";
const AuthenticationProvider = _ref => {
  let {
    config,
    children,
    loadingComponent = /* @__PURE__ */jsx(Loading, {})
  } = _ref;
  const [loading, setLoading] = useState(true);
  const [authData, setAuthData] = useState(__spreadProps(__spreadValues({}, emptyValue), {
    config
  }));
  useEffect(() => {
    const initialToken = async () => {
      const token = await acquireToken(config);
      if (token) {
        const initUser = async () => {
          const user2 = await getCurrentUser(token, config);
          if (config.app === "myempower") {
            user2.avatarName = getUserAvatarName(user2.firstName, user2.lastName);
            user2.photoBase64 = localStorage.getItem(LOCAL_STORAGE_KEY[config.app].USER_PHOTO);
          } else {
            if (isAMUser()) {
              const amInfo = JSON.parse(localStorage.getItem(AM_STORAGE_KEY.AM_INFO) || "{}");
              user2.firstName = amInfo.firstName;
              user2.lastName = amInfo.lastName;
            }
          }
          user2.fullName = formatFullName(user2.firstName, user2.lastName);
          return user2;
        };
        const modules = await getFeatures(token, config);
        const domains = await getUserDomains(token, config);
        const user = await initUser();
        let setUserDomain;
        if (config.app === "empower") {
          setUserDomain = (newToken, entitySelection) => saveUserDomain(newToken, config, entitySelection.id).then(() => {
            updateUser({
              entitySelection
            });
          });
        }
        const updateUser = newUser => {
          let updatedUser;
          if (typeof newUser === "function") {
            updatedUser = newUser(user);
          } else {
            updatedUser = newUser;
          }
          setAuthData(prev => __spreadProps(__spreadValues({}, prev), {
            user: __spreadValues(__spreadValues({}, prev.user), updatedUser)
          }));
        };
        const refreshUser = async () => {
          const user2 = await initUser();
          setAuthData(prev => __spreadProps(__spreadValues({}, prev), {
            user: user2
          }));
          return user2;
        };
        const refreshPage = () => {
          setAuthData(prev => __spreadProps(__spreadValues({}, prev), {
            refreshKey: prev.refreshKey + 1
          }));
        };
        setAuthData(__spreadProps(__spreadValues({}, authData), {
          config,
          token,
          user,
          modules,
          domains,
          setToken: newToken => {
            setAuthData(prev => __spreadProps(__spreadValues({}, prev), {
              token: newToken
            }));
          },
          cachedPermissions: {},
          // initiated by on-demand in usePermission
          setCachedPermissions: cachedPermissions => {
            setAuthData(prev => __spreadProps(__spreadValues({}, prev), {
              cachedPermissions
            }));
          },
          setUserDomain,
          updateUser,
          refreshUser,
          refreshPage
        }));
        setLoading(false);
      } else {
        throw new Error("Not able to acquire token, refer to above error.");
      }
    };
    initialToken().catch(error => {
      console.debug(error, "Redirecting to login...");
      loginRedirect(config);
    });
  }, []);
  if (loading) return loadingComponent;
  return /* @__PURE__ */jsx(AuthenticationContext.Provider, {
    value: authData,
    children
  });
};
export { AuthenticationContext, AuthenticationProvider };